// Generic math helpers for sampling timeline stops and mapping global vh into local segment vh.

import type { MotionStop } from '../types/homeScroll';

export const clamp = (value: number, min = 0, max = 1): number =>
  Math.min(Math.max(value, min), max);

export const lerp = (start: number, end: number, progress: number): number =>
  start + (end - start) * progress;

export const easeOutCubic = (value: number): number =>
  1 - Math.pow(1 - value, 3);

export const mapProgress = (
  value: number,
  start: number,
  end: number,
  easing?: (input: number) => number
): number => {
  const normalized = clamp((value - start) / Math.max(end - start, 0.0001));
  return easing ? easing(normalized) : normalized;
};

export const sampleStops = (stops: MotionStop[], localVh: number): number => {
  if (stops.length === 0) {
    return 0;
  }

  if (localVh <= stops[0]![0]) {
    return stops[0]![1];
  }

  for (let index = 1; index < stops.length; index += 1) {
    const previous = stops[index - 1]!;
    const current = stops[index]!;
    const progress = mapProgress(localVh, previous[0], current[0]);

    if (localVh <= current[0]) {
      return lerp(
        previous[1],
        current[1],
        current[2] === 'easeOut' ? easeOutCubic(progress) : progress
      );
    }
  }

  return stops[stops.length - 1]![1];
};

export const toLocalClipVh = (
  globalVh: number,
  startVh: number,
  durationVh: number,
  clipVh = 100
): number =>
  clamp(
    ((globalVh - startVh) / Math.max(durationVh, 0.0001)) * clipVh,
    0,
    clipVh
  );
